import { ART_PAL, body, edge, mix, n, shade, tint, warm, type ArtPal, type ArtTheme } from './covers/kit';

/*
 * Dou Dizhu (斗地主) role hats, 32 × 32 icons that read at 16–24 px beside a seat:
 *  - landlord (地主): a round lacquer-red skullcap (瓜皮帽): gores with gold seams (five seen, never four), a gold brim band with a
 *    small red gem and a red knot button on top — the one warm, rich mark at the table;
 *  - peasant (农民): a calm conical straw hat (斗笠) — straw gold, a few weave lines, a dark cord band.
 * No text, no glyphs; not green (no green headwear — docs/art-prompts.md). Role names for screen readers come from i18n.
 */

/** Hat box (viewBox). */
export const HAT_BOX = 32;

const doc = (inner: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${HAT_BOX} ${HAT_BOX}" width="${HAT_BOX}" height="${HAT_BOX}">${inner}</svg>`;

/** Landlord skullcap, inner markup (ids prefixed by `p`). */
export function landlordHatMarkup(P: ArtPal, p: string): string {
  const red = P.red;
  const gold = warm(P);
  const line = edge(red, P);
  // the dome: a half-ellipse over the band; gore seams curve towards the button
  const seams = [-0.7, -0.35, 0, 0.35, 0.7]
    .map((k) => `M${n(16 + k * 5)} 7.6Q${n(16 + k * 12)} 12 ${n(16 + k * 13)} 21`)
    .join('');
  return (
    `<defs>${body(`${p}r`, red, 0.35, 0.35, 0.7, 1)}${body(`${p}g`, gold, 0.4, 0.3, 0.3, 1)}</defs>` +
    `<ellipse cx="16" cy="27.6" rx="12" ry="2" fill="${P.dark ? '#000' : shade(P.ink, 0.2)}" fill-opacity="${P.dark ? 0.45 : 0.16}"/>` +
    `<path d="M3.2 21.5C3.2 12.6 8.6 7 16 7S28.8 12.6 28.8 21.5Z" fill="url(#${p}r)" stroke="${line}" stroke-width="1.1" stroke-linejoin="round"/>` +
    `<path d="${seams}" fill="none" stroke="${gold}" stroke-opacity=".85" stroke-width=".9" stroke-linecap="round"/>` +
    `<rect x="2.4" y="20.2" width="27.2" height="5.6" rx="2.6" fill="url(#${p}g)" stroke="${edge(gold, P)}" stroke-width="1"/>` +
    `<ellipse cx="16" cy="23" rx="2.5" ry="1.8" fill="${red}" stroke="${edge(gold, P)}" stroke-width=".7"/>` +
    `<path d="M16 7V4.8" stroke="${line}" stroke-width="1.2"/>` +
    `<circle cx="16" cy="3.6" r="2.4" fill="url(#${p}r)" stroke="${line}" stroke-width=".9"/>` +
    `<ellipse cx="9.6" cy="12.6" rx="3.2" ry="1.4" transform="rotate(-38 9.6 12.6)" fill="#FFF" fill-opacity=".45"/>` +
    `<circle cx="15.2" cy="2.9" r=".8" fill="#FFF" fill-opacity=".6"/>`
  );
}

/** Peasant straw hat (斗笠), inner markup (ids prefixed by `p`). */
export function peasantHatMarkup(P: ArtPal, p: string): string {
  const straw = P.dark ? mix(P.gold, P.orange, 0.3) : mix(tint(P.gold, 0.45), P.orange, 0.12);
  const line = edge(straw, P);
  const cord = P.dark ? mix(P.orange, '#5A3A2A', 0.6) : '#7A4A2E';
  // weave lines from the tip down the cone (odd count — no groups of four)
  const weave = [-0.66, -0.33, 0, 0.33, 0.66]
    .map((k) => `M16 4.2L${n(16 + k * 13)} ${n(21.6 + Math.abs(k) * -1.4)}`)
    .join('');
  return (
    `<defs>${body(`${p}s`, straw, 0.35, 0.3, 0.8, 1)}</defs>` +
    `<ellipse cx="16" cy="26.4" rx="13" ry="2" fill="${P.dark ? '#000' : shade(P.ink, 0.2)}" fill-opacity="${P.dark ? 0.45 : 0.16}"/>` +
    `<path d="M16 3L30.4 21.4Q16 25.6 1.6 21.4Z" fill="url(#${p}s)" stroke="${line}" stroke-width="1.1" stroke-linejoin="round"/>` +
    `<path d="${weave}" fill="none" stroke="${line}" stroke-opacity=".45" stroke-width=".7"/>` +
    `<path d="M8.2 14.4Q16 16.6 23.8 14.4" fill="none" stroke="${cord}" stroke-width="1.6" stroke-linecap="round"/>` +
    `<path d="M1.6 21.4Q16 25.6 30.4 21.4" fill="none" stroke="${shade(straw, 0.25)}" stroke-width="1.2" stroke-linecap="round"/>` +
    `<ellipse cx="11.4" cy="12.4" rx="2.8" ry="1.1" transform="rotate(-50 11.4 12.4)" fill="#FFF" fill-opacity=".4"/>`
  );
}

/** Standalone SVG of the landlord hat. */
export const landlordHatSvg = (theme: ArtTheme, prefix = `lh${theme[0]}`): string =>
  doc(landlordHatMarkup(ART_PAL[theme], prefix));
/** Standalone SVG of the peasant hat. */
export const peasantHatSvg = (theme: ArtTheme, prefix = `ph${theme[0]}`): string =>
  doc(peasantHatMarkup(ART_PAL[theme], prefix));

const uris = new Map<string, string>();
const cached = (key: string, make: () => string): string => {
  let u = uris.get(key);
  if (!u) {
    u = 'data:image/svg+xml;utf8,' + encodeURIComponent(make());
    uris.set(key, u);
  }
  return u;
};

/** Data URI of the landlord hat (cached per theme). */
export const landlordHatUri = (theme: ArtTheme): string => cached(`l${theme}`, () => landlordHatSvg(theme));
/** Data URI of the peasant hat (cached per theme). */
export const peasantHatUri = (theme: ArtTheme): string => cached(`p${theme}`, () => peasantHatSvg(theme));
